/**
 * Wealthica2 DB IO helpers
 *
 * Responsibilities:
 * - Fetch institutional metadata (name, logos) by (institution_identifier, provider_type)
 * - Encapsulate the contract with `investment_institutions`
 *
 * Notes:
 * - Demo invariant: demo metadata must be stored as (institution_identifier='demo', provider_type='demo')
 * - Non-fatal errors: callers handle absence of metadata gracefully
 */
import { supabase } from '@/integrations/supabase/client';

export interface InstitutionMetadata {
  institution_name: string | null;
  logo_url: string | null;
  logo_proxied_url: string | null;
}

// Canonical 'demo' invariant:
// We intentionally look up metadata by (institution_identifier, provider_type).
// The demo institution must be stored as (institution_identifier='demo', provider_type='demo').
// Frontend callers should pass ('demo','demo') for demo to avoid fallbacks.
export async function fetchInstitutionMetadata(
  institutionIdentifier: string,
  providerType: 'wealthica' | 'demo'
): Promise<InstitutionMetadata | null> {
  const { data, error } = await supabase
    .from('investment_institutions')
    .select('institution_name, logo_url, logo_proxied_url')
    .eq('institution_identifier', institutionIdentifier)
    .eq('provider_type', providerType)
    .maybeSingle();

  if (error) {
    // Non-fatal; callers will handle missing metadata
    console.warn('db-io: fetchInstitutionMetadata error', error);
  }

  return (data as InstitutionMetadata) ?? null;
}
